import React, { useCallback, useMemo, useContext, useEffect, useState } from 'react'
import { css } from '@emotion/react'
import capitalize from 'voca/capitalize'
import { colors, backendColors, getBestTrainingGoalFromIndexResults } from 'shared-ui'
import PageTitle from '../_general/PageTitle'
import ScoreIndicator from '../_general/ScoreIndicator'
import Table from '../_general/Table'
import AnalysisDate from '../analysis/AnalysisDate'
import RcpMark from '../analysis/RcpMark'
import BrowsePanel from '../_control/BrowsePanel'
import PitchListContext from '../../context/PitchList'
import { useSearchParams } from 'react-router'
import { MTM_LIST_TYPES, MTM_EVENT_TYPES, MTM_VARIABLES } from '../../utils/matomo'
import useTracker from '../../hooks/useTracker'
import FancyDropdown from '../_control/FancyDropdown'
import ViewportContext from '../../context/ViewPort'
import { useIntl, FormattedMessage } from 'react-intl'
import TruncatedSummaryText from '../analysis/TruncatedSummaryText'
import PreferredViewContext from '../../context/PreferredView'
import IndexGauge from '../index/IndexGauge'
import BootIndexIcon from '../analysis/BootIndexIcon'
import useRawState from '../../hooks/useRawState'
import ProfileContext from '../../context/Profile'
import useBridge from '../../hooks/useBridge'
import { getHashParams, setHashParams } from '../../utils/urlHashParams'
import SwitcherSimple from '../_control/SwitcherSimple'
import LocalStorage from '../../services/LocalStorage'
import gaugeIcon from '../../assets/icons/gauge.svg'
import { ReactSVG } from 'react-svg'

function getNumberFromPageLink(pageString) {
    if (pageString === undefined) return 1
    return String(pageString)
        ?.match(/page=(?<number>[\d]+)/)
        ?.groups
        ?.number
}

const resultsPerPage = 20

const indexFilterName = 'analysis-history-hasIndexResultsFilter'

export default function AnalysisOverview({ ...props }) {
    const track = useTracker()
    const { formatMessage } = useIntl()
    const { pitches } = useContext(PitchListContext)
    const [searchParams, setSearchParams] = useSearchParams()
    const { showCompactUI } = useContext(ViewportContext)

    const { isPerformanceMode } = useContext(PreferredViewContext)
    const { data: profile, isBusy: profileBusy } = useContext(ProfileContext)
    const allowPerformanceFeature = profile?.featureFlags?.includes('performance')
    const showPerformance = allowPerformanceFeature && isPerformanceMode

    // set a hash '#viewMode=performance|greenkeeping' to let analytics track each as a separate page.
    useEffect(() => {
        const hashState = getHashParams().viewMode
        const currentMode = showPerformance ? 'performance' : 'greenkeeping'
        if (hashState !== currentMode) setHashParams({ viewMode: currentMode })
    }, [showPerformance, searchParams])

    const [indexFilter, setIndexFilter] = useState(!!LocalStorage.getItem(indexFilterName))

    useEffect(() => {
        LocalStorage.setItem(indexFilterName, !!indexFilter)
    }, [indexFilter])

    const [pageNum, selectedPitch] = useMemo(() => {
        const page = parseInt(searchParams.get('page')) || 1
        const pitchParam = searchParams.get('pitch')
        const pitchId = (pitchParam === 'null' || !pitchParam) ? null : parseInt(pitchParam)
        return [page, pitchId]
    }, [searchParams])

    const [apiEndPoint, query] = useMemo(() => {
        const query = {
            itemsPerPage: resultsPerPage,
            page: pageNum,
        }

        if (showPerformance && indexFilter) query["exists[indexResults]"] = true

        if (profileBusy) return [undefined, query]

        return [
            selectedPitch ?
                `/api/current/pitches/${selectedPitch}/analyses${showPerformance ? '/index-results' : ''}`
                : `/api/current/surface-analyses${showPerformance ? '/index-results' : ''}`
            ,
            query
        ]
    }, [selectedPitch, pageNum, showPerformance, profileBusy, indexFilter])

    const {
        data: analyses = [],
        firstPage,
        lastPage,
        isBusy: analysesBusy,
    } = useRawState(apiEndPoint, query)

    const { data: pitchInfo = {} } = useRawState(
        selectedPitch ? `/api/current/pitches/${selectedPitch}` : undefined,
        {},
        [selectedPitch]
    )

    const sortableAnalyses = useMemo(() => {
        return analyses.map(a => ({
            ...a,
            pitchName: a.pitch?.name || pitchInfo?.name || 'Unknown Pitch',
        }))
    }, [analyses, pitchInfo])

    const getRowHref = useCallback((analysis) => {
        const analysisPageLink = `/pitch/${analysis.pitch.id}/analysis/${analysis.id}`

        if (showPerformance) {
            const indRes = analysis.indexResults

            if (!indRes || !indRes.length) return analysisPageLink

            let totalLatest = 0
            for (const indexResult of indRes) {
                const currentLatest = indexResult.latestTestTimestamp
                if (totalLatest < currentLatest) totalLatest = currentLatest
            }

            return `/pitch/${analysis.pitch.id}/index/?timestamp=${totalLatest}`
        } else {
            return analysisPageLink
        }
    }, [showPerformance])

    const pitchFilterOptions = useMemo(() => {
        const options = pitches.map(p => ({
            value: p.id,
            label: p.name
        }))
        options.unshift({
            value: null,
            label: formatMessage({ id: 'allSportsFields' })
        })
        return options
    }, [pitches])

    const handlePitchChange = useCallback((e) => {
        const pitchId = e.value
        setSearchParams({ pitch: pitchId, page: 1 })
        track({
            'event': MTM_EVENT_TYPES['option-select'],
            [MTM_VARIABLES['list-type']]: MTM_LIST_TYPES['pitch-select'],
            [MTM_VARIABLES['list-value']]: pitchId
        })
    }, [setSearchParams])

    const handlePageChange = useCallback((num) => {
        setSearchParams(prev => {
            prev.set('page', num)
            return prev
        })
    }, [setSearchParams])


    const { data: indexCategories, loading: indexCategoriesLoading } = useRawState(
        (showPerformance ? `/api/current/index-categories` : undefined),
        { pagination: false },
    )

    const { impactTitle, energyTitle, gripTitle, /* riskTitle */ } = useMemo(() => {
        if (indexCategoriesLoading || !indexCategories) return {}
        return {
            impactTitle: indexCategories.find(cat => cat.indexCategoryType === 'impact')?.name,
            energyTitle: indexCategories.find(cat => cat.indexCategoryType === 'energy')?.name,
            gripTitle: indexCategories.find(cat => cat.indexCategoryType === 'traction')?.name,
            /* riskTitle: indexCategories.find(cat => cat.indexCategoryType === 'risk')?.name, */
        }
    }, [indexCategories, indexCategoriesLoading])

    const { data: bootTypes } = useBridge('/api/current/frontend-bridge/boot-types')

    return (
        <div
            css={{
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'column',
                justifyContent: 'start',
            }}
            {...props}>
            <div css={style.header}>
                <PageTitle css={css`margin-bottom: 0;`}>
                    <span css={{ color: colors.main1 }}>
                        <FormattedMessage id='surfaceAnalysisOverview' />
                    </span>
                </PageTitle>
                <BrowsePanel
                    pageNum={pageNum}
                    setPageNum={handlePageChange}
                    firstPage={getNumberFromPageLink(firstPage)}
                    lastPage={getNumberFromPageLink(lastPage)}
                    busy={analysesBusy}
                />
                {!showCompactUI && <div css={css`display: flex; align-items: center;`}>
                    {showPerformance &&
                        <label
                            css={css`display: flex; align-items: center; cursor: pointer;`}
                            title={formatMessage({ id: 'toggleFilter' }) + ` - ` + formatMessage({ id: 'index' })}
                        >
                            <span span css={style.dropdownTitle} >
                                <ReactSVG
                                    src={gaugeIcon}
                                    css={css`
                                        width: 2em;
                                        height: 2em;
                                        color: ${colors.light};
                                    `}
                                />
                            </span>
                            <SwitcherSimple
                                onClick={() => setIndexFilter(prev => !prev)}
                                checked={indexFilter}
                                css={css`width: 60px;`}
                            />
                        </label>
                    }

                    <span css={style.dropdownTitle} >
                        <FormattedMessage id='showMenu' />
                    </span>
                    <FancyDropdown
                        value={pitchFilterOptions.find(o => o.value === selectedPitch)}
                        onChange={handlePitchChange}
                        options={pitchFilterOptions}
                        css={style.dropdown}
                    />
                </div>}
            </div>
            <Table
                data={analysesBusy ? [] : sortableAnalyses}
                loading={analysesBusy}
                fallback={formatMessage({ id: 'noAnalysesYet' })}
                rowKey={row => (row.id)}
                sortOn='date'
                sortDirection='desc'
                getRowHref={getRowHref}
                columns={[
                    {
                        key: 'date',
                        header: capitalize(formatMessage({ id: 'analysisDate' })),
                        renderData: r => <AnalysisDate
                            date={Number(r.date)}
                            timeZone={selectedPitch ? pitchInfo?.venue?.timeZone : r.pitch.venue?.timeZone}
                            css={css`line-height: 1.2em; white-space: nowrap;`}
                        />,
                        sortable: true,
                    },
                    {
                        key: 'pitchName',
                        header: capitalize(formatMessage({ id: 'sportsField' })),
                        renderData: (analysis) => {

                            let pitchSuitedFor
                            if (showPerformance && showCompactUI) {
                                pitchSuitedFor = getBestTrainingGoalFromIndexResults(
                                    analysis.filteredIndexResults,
                                    new Date(analysis.date * 1000)
                                )?.indexCategory?.name
                            }

                            return <div css={css`
                                display: grid;
                                width: max-content;
                                gap: 0.5em;
                            `}>
                                <div css={css`
                                    white-space: ${showCompactUI ? 'normal' : 'nowrap'};
                                    min-width: 10em;
                                `}>
                                    {analysis.pitchName}
                                </div>

                                {showPerformance && showCompactUI &&
                                    <div>
                                        {pitchSuitedFor}
                                    </div>
                                }
                            </div>
                        },
                        sortable: true,
                    },
                    {
                        key: 'summaryText',
                        header: capitalize(formatMessage({ id: 'summary' }).toLowerCase()),
                        renderData: r => <TruncatedSummaryText summary={r.summary} />,
                        wide: true
                    },
                    {
                        key: 'rawCertified',
                        header: capitalize(formatMessage({ id: 'rawCertified' })),
                        renderData: analysis => (
                            analysis.rawCertified === true
                                ? <RcpMark />
                                : formatMessage({ id: 'no' })
                        ),
                        sortable: true,
                    },
                    {
                        key: 'testGroups',
                        header: capitalize(formatMessage({ id: 'category' })),
                        renderData: (analysis) => {
                            let string = ''
                            for (const group of analysis.testGroups) {
                                string += `${capitalize(group)}, `
                            }
                            return string.substr(0, string.length - 2) || 'None'
                        },
                    },
                    {
                        key: 'numberOfTests',
                        header: capitalize(formatMessage({ id: 'tests' })),
                        sortable: true,
                        renderData: (analysis) => analysis?.numberOfTests ?? analysis?.tests?.length
                    },
                    {
                        key: 'score',
                        header: capitalize(formatMessage({ id: 'score' })),
                        renderData: analysis => (
                            <ScoreIndicator
                                value={analysis.score}
                                color={backendColors[analysis.assessment]}
                                css={css`font-size: 1em;`}
                            />
                        ),
                        sortable: true,
                        shrink: true,
                    },
                    {
                        key: 'bestSuitedFor',
                        header: formatMessage({ id: 'bestSuitedFor' }),
                        renderData: analysis => {
                            return getBestTrainingGoalFromIndexResults(
                                analysis.filteredIndexResults,
                                new Date(analysis.date * 1000)
                            )?.indexCategory?.name
                        },
                        sortable: false,
                    },
                    {
                        key: 'INDEX-impact',
                        header: impactTitle,
                        renderData: analysis => {
                            const indRes = analysis.filteredIndexResults
                                .find(indR => indR.indexCategory.indexCategoryType === 'impact')
                            if (!indRes) return
                            return <IndexGauge
                                indexData={{
                                    localisedName: indRes.indexCategory.name,
                                    indexValue: indRes.value,
                                    frontendCode: indRes.score.assessment?.frontendCode,
                                    indexType: 'performance'
                                }}
                                size={90}
                                showName={false}
                            />
                        },
                        sortable: false,
                        shrink: true,
                    },
                    {
                        key: 'INDEX-energy',
                        header: energyTitle,
                        renderData: analysis => {
                            const indRes = analysis.filteredIndexResults
                                .find(indR => indR.indexCategory.indexCategoryType === 'energy')
                            if (!indRes) return
                            return <IndexGauge
                                indexData={{
                                    localisedName: indRes.indexCategory.name,
                                    indexValue: indRes.value,
                                    frontendCode: indRes.score.assessment?.frontendCode,
                                    indexType: 'performance'
                                }}
                                size={90}
                                showName={false}
                            />
                        },
                        sortable: false,
                        shrink: true,
                    },
                    {
                        key: 'INDEX-grip',
                        header: gripTitle,
                        renderData: analysis => {
                            const indRes = analysis.filteredIndexResults
                                .find(indR => indR.indexCategory.indexCategoryType === 'traction')
                            if (!indRes) return
                            return <IndexGauge
                                indexData={{
                                    localisedName: indRes.indexCategory.name,
                                    indexValue: indRes.value,
                                    frontendCode: indRes.score.assessment?.frontendCode,
                                    indexType: 'performance'
                                }}
                                size={90}
                                showName={false}
                            />
                        },
                        sortable: false,
                        shrink: true,
                    },
                    {
                        key: 'boot',
                        header: formatMessage({ id: 'wear' }),
                        renderData: analysis => {
                            const indRes = analysis.filteredIndexResults
                                .find(indR => indR.indexCategory.indexCategoryType === 'boot')
                            if (!indRes) return
                            const recommededBootType = indRes.bootRecommendations?.[0]?.bootType
                            return <div css={css`
                                white-space: ${showCompactUI ? 'unset' : 'nowrap'};
                                display: grid;
                                place-items: center;
                                place-self: center;
                                width: 100%;
                            `}>
                                <BootIndexIcon
                                    bootBackendCode={recommededBootType}
                                    bootNameLocalised={bootTypes?.[recommededBootType]}
                                    hoverOverlayText={showCompactUI}
                                    style={{width: '1.4em'}}
                                />
                                {showCompactUI ? null : bootTypes?.[recommededBootType]}
                            </div>
                        },
                        sortable: false,
                        shrink: true,
                    }
                ].filter(col => {
                    if (showPerformance) {
                        return col.key.includes('INDEX') || (
                            col.key === 'date' ||
                            col.key === 'pitchName' ||
                            (col.key === 'summaryText' && !showCompactUI) ||
                            (col.key === 'bestSuitedFor' && !showCompactUI) ||
                            col.key === 'boot'
                        )
                    } else {
                        return (
                            (col.key === 'date') ||
                            (col.key === 'pitchName') ||
                            (col.key === 'score') ||
                            (col.key === 'numberOfTests') ||
                            (col.key === 'rawCertified' && !showCompactUI) ||
                            (col.key === 'testGroups' && !showCompactUI) ||
                            (col.key === 'numberOfTests' && !showCompactUI) ||
                            (col.key === 'summaryText' && !showCompactUI)
                        )
                    }
                })}
                css={css`
                    flex-grow: 1;

                    ${showPerformance ? `
                    @media screen and (max-width: 1024px) {
                        font-size: 0.9em;
                    }
                    @media screen and (max-width: 720px) {
                        font-size: 0.7em;
                    }` : ''}
                `}
            />
            {!analysesBusy &&
                <BrowsePanel
                    pageNum={pageNum}
                    setPageNum={handlePageChange}
                    firstPage={getNumberFromPageLink(firstPage)}
                    lastPage={getNumberFromPageLink(lastPage)}
                    busy={analysesBusy}
                    css={css`margin-top: 1em;`}
                    grow={0}
                />
            }
        </div>
    )
}

const style = {
    dropdown: css`
        width: 16em;
        z-index: 2000;
    `,
    dropdownTitle: css`
        color: ${colors.subtle};
        font-size: 0.875em;
        font-weight: 300;
        margin-right: 0.75em;
    `,
    header: css`
        display: flex; 
        justify-content: stretch; 
        flex-wrap: wrap; gap: 1em;
        margin-bottom: 2em;
    `,
}